import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, Copy, Loader2, Search, X } from 'lucide-react';
import { apolloClient } from '../../services/graphql';
import { GET_CUSTOMERS } from '../../graphql/customers';
import {
  COPY_USER_CUSTOMER_ACCESS,
  GET_USER_CUSTOMER_ACCESS,
  GRANT_ALL_USER_CUSTOMER_ACCESS,
  GRANT_USER_CUSTOMER_ACCESS,
  REVOKE_USER_CUSTOMER_ACCESS,
  SET_ALL_USER_CUSTOMER_ACCESS_PERMISSIONS,
  UPDATE_USER_CONDITIONS_ACCESS,
  UPDATE_USER_CUSTOMER_ACCESS,
} from '../../graphql/user-customer-access';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ConfirmModal } from '../../framework/modals/ConfirmModal';
import { Modal } from '../ui/Modal';

interface Customer {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

interface FeatureAccessPermissions {
  canAdd: boolean;
  canChange: boolean;
  canDelete: boolean;
}

interface CustomerAccessGrant {
  canAdd: boolean;
  canChange: boolean;
  canDelete: boolean;
  appointmentDetailsAccess: FeatureAccessPermissions;
  customer: Customer;
}

interface UserModeratorAccessSettings {
  grants: CustomerAccessGrant[];
  canManageConditions: boolean;
}

interface AccessUser {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

interface UserCustomerAccessModalProps {
  open: boolean;
  user: AccessUser | null;
  allUsers: AccessUser[];
  onClose: () => void;
  onUpdated: () => void;
}

const READ_ONLY_PERMISSIONS = {
  canAdd: false,
  canChange: false,
  canDelete: false,
  appointmentDetailsAccess: {
    canAdd: false,
    canChange: false,
    canDelete: false,
  },
};

const FULL_PERMISSIONS = {
  canAdd: true,
  canChange: true,
  canDelete: true,
  appointmentDetailsAccess: {
    canAdd: true,
    canChange: true,
    canDelete: true,
  },
};

function PermissionCheckbox({
  label,
  checked,
  disabled,
  onChange,
  hideLabel = false,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  hideLabel?: boolean;
}) {
  return (
    <label
      className="inline-flex items-center justify-center gap-1.5 text-xs cursor-pointer select-none w-full"
      title={hideLabel ? label : undefined}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded"
        aria-label={label}
      />
      {!hideLabel ? (
        <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      ) : (
        <span className="sm:hidden" style={{ color: 'var(--color-text-secondary)' }}>
          {label}
        </span>
      )}
    </label>
  );
}

const GRANTED_CUSTOMERS_GRID =
  'grid items-center gap-x-2 px-4 [grid-template-columns:minmax(0,1fr)_4.5rem_4.75rem_4.5rem_5.5rem]';

type PermissionField = 'canAdd' | 'canChange' | 'canDelete';

type ConfirmAction =
  | { type: 'copy-from'; fromUserId: string; label: string }
  | { type: 'remove'; customerId: string; customerName: string };

export const UserCustomerAccessModal: React.FC<UserCustomerAccessModalProps> = ({
  open,
  user,
  allUsers,
  onClose,
  onUpdated,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [canManageConditions, setCanManageConditions] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const customerPickerRef = useRef<HTMLDivElement>(null);
  const copyPickerRef = useRef<HTMLDivElement>(null);

  useClickOutside(customerPickerRef, () => setShowPicker(false));
  useClickOutside(copyPickerRef, () => setShowCopyPicker(false));

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setShowPicker(false);
      setCopySearch('');
      setShowCopyPicker(false);
      setActionError(null);
      setCanManageConditions(false);
      setConfirmAction(null);
    }
  }, [open]);

  const userId = user?.id ?? '';

  const { data: accessSettings, isLoading: grantedLoading, refetch: refetchGranted } = useQuery({
    queryKey: ['userCustomerAccess', userId],
    enabled: open && !!userId,
    queryFn: async () => {
      const { data } = await apolloClient.query({
        query: GET_USER_CUSTOMER_ACCESS,
        variables: { userId },
        fetchPolicy: 'network-only',
      });
      return data.userCustomerAccess as UserModeratorAccessSettings;
    },
  });

  useEffect(() => {
    if (accessSettings) {
      setCanManageConditions(accessSettings.canManageConditions);
    }
  }, [accessSettings]);

  const { data: customersData } = useQuery({
    queryKey: ['customersForAccess'],
    enabled: open,
    queryFn: async () => {
      const { data } = await apolloClient.query({
        query: GET_CUSTOMERS,
        fetchPolicy: 'network-only',
      });
      return (data.customers as Customer[]).filter((c) => c.isActive);
    },
  });

  const grantedGrants = accessSettings?.grants ?? [];
  const grantedIds = useMemo(
    () => new Set(grantedGrants.map((grant) => grant.customer.id)),
    [grantedGrants]
  );

  const availableCustomers = useMemo(() => {
    const active = (customersData ?? []).filter((c) => !grantedIds.has(c.id));
    const term = searchTerm.trim().toLowerCase();
    if (!term) return active;
    return active.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        (c.description?.toLowerCase().includes(term) ?? false)
    );
  }, [customersData, grantedIds, searchTerm]);

  const copyCandidates = useMemo(() => {
    const term = copySearch.trim().toLowerCase();
    return allUsers
      .filter((u) => u.id !== userId && u.role !== 'ADMINISTRATOR')
      .filter((u) => {
        if (!term) return true;
        const label = `${u.firstName ?? ''} ${u.lastName ?? ''} ${u.username}`.toLowerCase();
        return label.includes(term);
      });
  }, [allUsers, copySearch, userId]);

  const allCustomersGranted =
    (customersData?.length ?? 0) > 0 &&
    grantedGrants.length >= (customersData?.length ?? 0);

  const refresh = async () => {
    await refetchGranted();
    onUpdated();
  };

  const grantMutation = useMutation({
    mutationFn: async (customerId: string) => {
      await apolloClient.mutate({
        mutation: GRANT_USER_CUSTOMER_ACCESS,
        variables: { userId, customerId, permissions: READ_ONLY_PERMISSIONS },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      setSearchTerm('');
      setShowPicker(false);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const revokeMutation = useMutation({
    mutationFn: async (customerId: string) => {
      await apolloClient.mutate({
        mutation: REVOKE_USER_CUSTOMER_ACCESS,
        variables: { userId, customerId },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const grantAllMutation = useMutation({
    mutationFn: async () => {
      await apolloClient.mutate({
        mutation: GRANT_ALL_USER_CUSTOMER_ACCESS,
        variables: { userId },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: async ({
      customerId,
      permissions,
    }: {
      customerId: string;
      permissions: typeof FULL_PERMISSIONS;
    }) => {
      await apolloClient.mutate({
        mutation: UPDATE_USER_CUSTOMER_ACCESS,
        variables: { userId, customerId, permissions },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateConditionsMutation = useMutation({
    mutationFn: async (value: boolean) => {
      await apolloClient.mutate({
        mutation: UPDATE_USER_CONDITIONS_ACCESS,
        variables: { userId, canManageConditions: value },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const bulkPermissionsMutation = useMutation({
    mutationFn: async (permissions: typeof FULL_PERMISSIONS) => {
      await apolloClient.mutate({
        mutation: SET_ALL_USER_CUSTOMER_ACCESS_PERMISSIONS,
        variables: {
          userId,
          permissions,
          includeConditions: true,
        },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const copyMutation = useMutation({
    mutationFn: async (fromUserId: string) => {
      await apolloClient.mutate({
        mutation: COPY_USER_CUSTOMER_ACCESS,
        variables: { fromUserId, toUserId: userId },
      });
    },
    onSuccess: async () => {
      setActionError(null);
      setCopySearch('');
      setShowCopyPicker(false);
      await refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const handleCopyFromUser = (fromUser: AccessUser) => {
    const label =
      fromUser.firstName && fromUser.lastName
        ? `${fromUser.firstName} ${fromUser.lastName}`
        : fromUser.username;
    setConfirmAction({ type: 'copy-from', fromUserId: fromUser.id, label });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'copy-from') {
      await copyMutation.mutateAsync(confirmAction.fromUserId);
    } else if (confirmAction.type === 'remove') {
      await revokeMutation.mutateAsync(confirmAction.customerId);
    }

    setConfirmAction(null);
  };

  const confirmModalProps = (() => {
    if (!confirmAction) return null;

    if (confirmAction.type === 'copy-from') {
      return {
        title: 'Copy customer access',
        message: `Replace this user's customer access with permissions from ${confirmAction.label}?`,
        confirmLabel: 'Copy access',
        type: 'warning' as const,
      };
    }

    return {
      title: 'Remove customer access',
      message: `Remove access to ${confirmAction.customerName}? This user will no longer be able to view that customer's data.`,
      confirmLabel: 'Remove',
      type: 'danger' as const,
    };
  })();

  const handleGrantPermissionChange = (
    grant: CustomerAccessGrant,
    field: PermissionField,
    value: boolean
  ) => {
    const permissions = {
      canAdd: field === 'canAdd' ? value : grant.canAdd,
      canChange: field === 'canChange' ? value : grant.canChange,
      canDelete: field === 'canDelete' ? value : grant.canDelete,
    };
    updatePermissionsMutation.mutate({
      customerId: grant.customer.id,
      permissions: {
        ...permissions,
        appointmentDetailsAccess: { ...permissions },
      },
    });
  };

  const isBusy =
    grantMutation.isPending ||
    revokeMutation.isPending ||
    grantAllMutation.isPending ||
    copyMutation.isPending ||
    updatePermissionsMutation.isPending ||
    updateConditionsMutation.isPending ||
    bulkPermissionsMutation.isPending;

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.username ?? '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Customer access — ${displayName}`}
      maxWidthClassName="max-w-4xl"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
            }}
          >
            Close
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="relative flex-1" ref={customerPickerRef}>
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--color-text-muted)' }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowPicker(true);
              }}
              onFocus={() => setShowPicker(true)}
              placeholder="Search customers to add..."
              className="w-full pl-10 pr-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
              }}
            />
            {showPicker && availableCustomers.length > 0 && (
              <div
                className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border shadow-lg dark-scrollbar"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                }}
              >
                {availableCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    disabled={isBusy}
                    onClick={() => grantMutation.mutate(customer.id)}
                    className="w-full text-left px-3 py-2 hover:opacity-80 transition-opacity"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    <div className="text-sm font-medium">{customer.name}</div>
                    {customer.description && (
                      <div className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                        {customer.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            disabled={isBusy || allCustomersGranted}
            onClick={() => grantAllMutation.mutate()}
            className="px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap disabled:opacity-50"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white',
            }}
          >
            {grantAllMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin inline" />
            ) : (
              'Select All'
            )}
          </button>
        </div>

        <div className="relative" ref={copyPickerRef}>
          <button
            type="button"
            onClick={() => setShowCopyPicker((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)',
              backgroundColor: 'var(--color-bg-secondary)',
            }}
          >
            <Copy className="w-4 h-4" />
            Copy from user
          </button>

          {showCopyPicker && (
            <div
              className="mt-2 rounded-lg border p-3 space-y-2"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <input
                type="text"
                value={copySearch}
                onChange={(e) => setCopySearch(e.target.value)}
                placeholder="Search users..."
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              />
              <div className="max-h-36 overflow-y-auto dark-scrollbar space-y-1">
                {copyCandidates.length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    No eligible users found.
                  </p>
                ) : (
                  copyCandidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleCopyFromUser(candidate)}
                      className="w-full text-left px-2 py-1.5 rounded hover:opacity-80 text-sm"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {candidate.firstName && candidate.lastName
                        ? `${candidate.firstName} ${candidate.lastName}`
                        : candidate.username}
                      <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        @{candidate.username}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className="rounded-lg border px-4 py-3"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}
        >
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={canManageConditions}
              disabled={isBusy}
              onChange={(e) => {
                const next = e.target.checked;
                setCanManageConditions(next);
                updateConditionsMutation.mutate(next);
              }}
              className="rounded"
            />
            <span style={{ color: 'var(--color-text-primary)' }}>Conditions</span>
          </label>
        </div>

        {actionError && (
          <p className="text-sm" style={{ color: 'var(--color-error)' }}>
            {actionError}
          </p>
        )}

        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div
            className="px-4 py-2 border-b flex items-center justify-between gap-3"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <div className="flex items-center gap-2 min-w-0 text-sm font-medium">
              <Building2 className="w-4 h-4 shrink-0" />
              <span className="truncate">Granted customers ({grantedGrants.length})</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => bulkPermissionsMutation.mutate(FULL_PERMISSIONS)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50"
                style={{
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'var(--color-bg-primary)',
                }}
              >
                Grant All
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => bulkPermissionsMutation.mutate(READ_ONLY_PERMISSIONS)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50"
                style={{
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)',
                  backgroundColor: 'var(--color-bg-primary)',
                }}
              >
                Revoke All
              </button>
            </div>
          </div>

          {grantedLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-accent)' }} />
            </div>
          ) : grantedGrants.length === 0 ? (
            <p className="px-4 py-6 text-sm text-center" style={{ color: 'var(--color-text-muted)' }}>
              No customers assigned — this user cannot view any customer data.
            </p>
          ) : (
            <>
              <div
                className={`${GRANTED_CUSTOMERS_GRID} py-2 border-b text-xs font-normal`}
                style={{
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-muted)',
                }}
              >
                <span className="hidden sm:block" />
                <span className="hidden sm:block text-center">Add</span>
                <span className="hidden sm:block text-center">Change</span>
                <span className="hidden sm:block text-center">Delete</span>
                <span className="hidden sm:block text-center">Remove</span>
              </div>
              <ul className="max-h-80 overflow-y-auto dark-scrollbar divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {grantedGrants.map((grant) => (
                <li key={grant.customer.id} className={`${GRANTED_CUSTOMERS_GRID} py-3`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                      {grant.customer.name}
                    </div>
                    {grant.customer.description && (
                      <div className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                        {grant.customer.description}
                      </div>
                    )}
                  </div>

                  <PermissionCheckbox
                    label="Add"
                    checked={grant.canAdd}
                    disabled={isBusy}
                    hideLabel
                    onChange={(value) => handleGrantPermissionChange(grant, 'canAdd', value)}
                  />
                  <PermissionCheckbox
                    label="Change"
                    checked={grant.canChange}
                    disabled={isBusy}
                    hideLabel
                    onChange={(value) => handleGrantPermissionChange(grant, 'canChange', value)}
                  />
                  <PermissionCheckbox
                    label="Delete"
                    checked={grant.canDelete}
                    disabled={isBusy}
                    hideLabel
                    onChange={(value) => handleGrantPermissionChange(grant, 'canDelete', value)}
                  />
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() =>
                      setConfirmAction({
                        type: 'remove',
                        customerId: grant.customer.id,
                        customerName: grant.customer.name,
                      })
                    }
                    className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-xs font-medium w-full"
                    style={{
                      color: 'var(--color-error)',
                      backgroundColor: 'var(--color-error-bg)',
                    }}
                  >
                    <X className="w-3 h-3" />
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            </>
          )}
        </div>

        {confirmModalProps && (
          <ConfirmModal
            isOpen={!!confirmAction}
            onClose={() => setConfirmAction(null)}
            onConfirm={handleConfirmAction}
            title={confirmModalProps.title}
            message={confirmModalProps.message}
            type={confirmModalProps.type}
            confirmLabel={confirmModalProps.confirmLabel}
            isLoading={isBusy}
          />
        )}
      </div>
    </Modal>
  );
};
